import * as React from 'react'
import queryString from 'query-string'
import axios, { AxiosResponse } from 'axios'
import { useDebounce } from 'react-use'
import { useAddToCart, useLocale } from 'hooks'
import { tracking, withTranslations, encodeUriWith, formatPrice } from 'helpers'
import { SHIPPING_METHODS } from 'types/shipping'
import WalletPayment from '../../payments/WalletPayment'
import ProductHeader from '../ProductHeader'
import ProductSizeOptions from '../ProductSizeOptions'
import ProductDetails from '../ProductDetails'
import ProductActions from '../ProductActions'
import ProductDeliveryLabel from '../ProductDeliveryLabel'
import {
  SizeConversion,
  SizeConversionIdsAndPrices
} from '../../../types/sizeConversion'
import ProductUspList from '../ProductUspList'
import BuyerPriceVisualizer from '../ProductPriceBreakdown'
import { ProductInfoProps } from './ProductInfo.types'

const ProductInfo: React.FC<ProductInfoProps> = ({
  productInfo,
  isWalletPaymentOn,
  stripePublishableKey,
  stripePublishableKeys,
  showStockNotifyBtn = true,
  selectedSizeConversionId,
  setSelectedSizeConversionId,
  selectedPriceCents,
  setSelectedPriceCents,
  inStock,
  selectedFormattedDisplayPrice,
  setSelectedFormattedDisplayPrice,
  selectedId,
  setSelectedId
}) => {
  const {
    slug,
    styleCode,
    title,
    contentCategory,
    brand,
    colour,
    displayCurrencyCode,
    productId,
    imageUrl,
    selectedPriceCents: initialSelectedPriceCents,
    sizeConversionIdsAndPrices,
    captain,
    description = '',
    details = {},
    allSizeConversions,
    infoCallout = {},
    fittingNote = null,
    groupType = 'sizes',
    defaultSizeSystem: initialSelectedSizeSystem
  } = productInfo
  const { localiseUrl } = useLocale()

  const [loadingWalletPayment, setLoadingWalletPayment] = React.useState(false)

  const isPremiumShippable = sizeConversionIdsAndPrices.some(
    (obj) => obj.shipping_method === SHIPPING_METHODS.PREMIUM
  )

  const selectedPrice = selectedPriceCents
    ? formatPrice(selectedPriceCents, {
        currencyCode: null,
        valueIsCents: true,
        precise: true
      })
    : null

  const { addToCart, cartItemId, categoryName } = useAddToCart(
    productInfo,
    selectedSizeConversionId,
    selectedPrice,
    selectedId
  )

  const initialSelectedPrice = initialSelectedPriceCents
    ? formatPrice(initialSelectedPriceCents, {
        currencyCode: null,
        valueIsCents: true,
        precise: true
      })
    : null

  const [variableStripePublishableKey, setVariableStripePublishableKey] =
    React.useState(stripePublishableKey)

  React.useEffect(() => {
    if (categoryName === undefined) return
    tracking.trackMetaEvent('ViewContent', {
      content_name: title,
      content_category: contentCategory,
      content_ids: [styleCode],
      content_type: 'product_group',
      value: initialSelectedPrice,
      currency: displayCurrencyCode
    })
  }, [
    // All from props, so this will run on mount and whenever these props change
    brand,
    categoryName,
    colour,
    displayCurrencyCode,
    imageUrl,
    initialSelectedPrice,
    productId,
    styleCode,
    title,
    contentCategory
  ])

  const renewStripeKey = () => {
    setLoadingWalletPayment(true)

    axios
      .get(
        localiseUrl(`/api/selling/${selectedId}/stripe_publishable_key`, {
          localeAsParam: true
        })
      )
      .then((res) => {
        setVariableStripePublishableKey(res.data.stripe_publishable_key)
      })
      .catch((error) => {
        // TODO: handle errors better
        console.error(error)
      })
      .finally(() => {
        setLoadingWalletPayment(false)
      })
  }

  // renew stripe key whenever collection id (size) changes
  // in case the collections region is different
  // but debounce it in case the user switches very fast
  useDebounce(renewStripeKey, 500, [selectedId])
  // and set wallet pay to loading whenever size changes
  React.useEffect(() => {
    setLoadingWalletPayment(true)
  }, [selectedId])

  const [fullPriceBreakdown, setFullPriceBreakdown] = React.useState(
    productInfo.sizeConversionIdsAndPrices.find(
      (stockItem) => stockItem.size_conversion_id === selectedSizeConversionId
    )?.cleansed_price_breakdown
  )

  const handleSizeConversionSelect = (
    newSelectedSizeConversion: SizeConversion
  ) => {
    const newSelectedCollection: SizeConversionIdsAndPrices | undefined =
      sizeConversionIdsAndPrices
        .sort((sp) =>
          sp.shipping_method === SHIPPING_METHODS.STANDARD ? -1 : 1
        )
        .find((sp) => sp.size_conversion_id === newSelectedSizeConversion.id)

    if (!newSelectedCollection) {
      return
    }

    if (newSelectedCollection.id === selectedId) {
      return
    }

    setSelectedSizeConversionId(newSelectedCollection.size_conversion_id)
    setSelectedPriceCents(newSelectedCollection.display_price_cents)
    setSelectedFormattedDisplayPrice(
      newSelectedCollection.formatted_display_price
    )
    setSelectedId(newSelectedCollection.id)
    setFullPriceBreakdown(newSelectedCollection.cleansed_price_breakdown)

    if (window.history.replaceState) {
      const { url, query } = queryString.parseUrl(window.location.toString())

      query.uk_size = newSelectedSizeConversion.uk
      query.eu_size = newSelectedSizeConversion.eu
      query.us_size = newSelectedSizeConversion.us

      const newUrl = `${url}?${queryString.stringify(query)}`

      try {
        window.history.replaceState({ path: newUrl }, '', newUrl)
      } catch (err) {
        console.error(err)
      }
    }
  }

  const removeFromCartRequest = (redirect = true) => {
    const jsonType = 'application/json'

    /** @returns {boolean} success */
    const afterFunc = (res: AxiosResponse) => {
      const { data } = res

      if (redirect) {
        window.location.href = encodeUriWith(
          'encodeURI',
          localiseUrl(`${window.location.origin}${data.path}`)
        )
      }

      return res.status === 200
    }

    return axios
      .delete(
        localiseUrl(`/cart/${cartItemId.current}`, { localeAsParam: true }),
        {
          headers: {
            'Content-Type': jsonType,
            Accept: jsonType
          }
        }
      )
      .then(afterFunc)
      .catch((res) => afterFunc(res.response))
  }

  const removeFromCart = async (shouldRedirect = true) => {
    const success = await removeFromCartRequest(shouldRedirect)

    if (success) {
      tracking.pushToDataLayer('eec.removeFromCart', {
        ecommerce: {
          currencyCode: displayCurrencyCode,
          remove: {
            products: [
              {
                name: title,
                id: styleCode,
                price: selectedPrice,
                brand,
                category: categoryName,
                variant: colour,
                quantity: 1,
                list: 'Product Page'
              }
            ]
          }
        }
      })
    }
  }

  const handleExpressCheckoutCancel = () => {
    removeFromCart(false)
  }

  const handleAddToCart = () => {
    tracking.trackFacebookEvent('AddToCart', productInfo, selectedPrice)

    addToCart(true)
  }

  const handleExpressCheckout = () => {
    tracking.trackFacebookEvent('ExpressCheckout', productInfo, selectedPrice)

    addToCart(false)
  }

  const hasSubText = Object.keys(infoCallout).length === 0 || !!fittingNote

  const walletPaymentElement = (
    <WalletPayment
      isPdp
      currencyCode={displayCurrencyCode}
      cart={[
        {
          ProductName: title,
          ItemPrice: String(selectedPrice),
          ItemCurrency: displayCurrencyCode
        }
      ]}
      prepareForPayment={handleExpressCheckout}
      teardownFromPayment={handleExpressCheckoutCancel}
      currentStripePublishableKey={variableStripePublishableKey}
      stripePublishableKeys={stripePublishableKeys}
      loadingWalletPayment={loadingWalletPayment}
    />
  )

  return (
    <div data-testid="product-info">
      <ProductHeader
        className={`hide-mobile ${hasSubText && 'has-subtext'}`}
        captain={captain}
        title={title}
      />

      <ProductSizeOptions
        allSizeConversions={allSizeConversions}
        sizeConversionIdsAndPrices={sizeConversionIdsAndPrices}
        selectedSizeConversionId={selectedSizeConversionId}
        selectedFormattedDisplayPrice={selectedFormattedDisplayPrice}
        infoCallout={infoCallout}
        fittingNote={fittingNote}
        groupType={groupType}
        onSizeSelect={handleSizeConversionSelect}
        defaultSizeSystem={initialSelectedSizeSystem}
      />

      {isPremiumShippable && (
        <ProductDeliveryLabel
          visible={sizeConversionIdsAndPrices.some(
            (obj) =>
              obj.shipping_method === SHIPPING_METHODS.PREMIUM &&
              obj.size_conversion_id === selectedSizeConversionId
          )}
          groupType={groupType}
        />
      )}

      {!!fullPriceBreakdown && (
        <BuyerPriceVisualizer fullPriceBreakdown={fullPriceBreakdown} />
      )}

      <ProductActions
        slug={slug}
        isWalletPaymentOn={isWalletPaymentOn}
        inStock={inStock}
        handleAddToCart={handleAddToCart}
        walletPaymentElement={walletPaymentElement}
        showStockNotifyBtn={showStockNotifyBtn}
        styleCode={styleCode}
      />

      <ProductUspList premiumShippable={isPremiumShippable} />

      <ProductDetails description={description} details={details} />
    </div>
  )
}

export default withTranslations(ProductInfo)
